import React, { useMemo } from 'react';
import cn from 'classnames';

import { getTranslation } from 'helpers/getTranslation';

import { Drawer } from 'components/shared/Drawer';
import { Button } from 'components/shared/Button';
import { Checkbox } from 'components/shared/Checkbox';
import { Typography } from 'components/shared/Typography';
import { Loading } from 'components/shared/Loading';
import { Select } from 'components/shared/Select';

import { CoursePreview } from 'components/CoursePreview';
import { SchoolPreview } from 'components/SchoolPreview';
import { Tooltip } from 'components/Tooltip';

import type {
  LessonOrderOption,
  LinkedCourse,
} from 'store/courses-lessons/helpers';

import {
  isModularCourse,
  type CourseModuleOption,
} from './LessonAttachCoursesDrawer.helpers';

import styles from './LessonAttachCoursesDrawer.module.scss';

type CourseWithTypeLabel = LinkedCourse & {
  typeLabel?: string;
};

type ModuleSelectOption = {
  label: string;
  value: string;
};

const LessonAttachCoursesDrawer = ({
  open,
  lessonTitle,
  loading,
  saving,
  canSave,
  courses,
  attachedCourseIds,
  initialAttachedCourseIds,
  modulesByCourseId,
  modulesLoadingCourseIds,
  draftModuleByCourseId,
  draftOrderByCourseId,
  orderOptionsByCourseId,
  onClose,
  onCancel,
  onSave,
  onCourseToggle,
  onModuleChange,
  onOrderChange,
}: {
  open: boolean;
  lessonTitle: string;
  loading: boolean;
  saving: boolean;
  canSave: boolean;
  courses: CourseWithTypeLabel[];
  attachedCourseIds: Set<string>;
  initialAttachedCourseIds: Set<string>;
  modulesByCourseId: Record<string, CourseModuleOption[]>;
  modulesLoadingCourseIds: Set<string>;
  draftModuleByCourseId: Record<string, string>;
  draftOrderByCourseId: Record<string, number>;
  orderOptionsByCourseId: Record<string, LessonOrderOption[]>;
  onClose: () => void;
  onCancel: () => void;
  onSave: () => void;
  onCourseToggle: (course: LinkedCourse, isAttached: boolean) => void;
  onModuleChange: (courseId: string, moduleId: string) => void;
  onOrderChange: (courseId: string, order: number) => void;
}) => {
  const isInteractionDisabled = loading || saving;

  const moduleOptionsByCourseId = useMemo(() => {
    const optionsByCourseId: Record<string, ModuleSelectOption[]> = {};

    for (const [courseId, modules] of Object.entries(modulesByCourseId)) {
      optionsByCourseId[courseId] = modules.map((module) => ({
        label: module.title || getTranslation('common.text.untitled'),
        value: module.id,
      }));
    }

    return optionsByCourseId;
  }, [modulesByCourseId]);

  return (
    <Drawer
      open={open}
      headerClassName={styles.drawerHeader}
      headerContent={getTranslation('courses_lessons_page.attach_drawer.header')}
      onClose={onClose}
    >
      <div className={styles.body}>
        <div className={styles.content}>
          <Typography
            className={styles.lessonSubtitle}
            variant="body3"
            mode="compact"
            title={lessonTitle}
          >
            {getTranslation('courses_lessons_page.attach_drawer.lesson_label', {
              lessonTitle,
            })}
          </Typography>

          {loading ? (
            <div className={styles.loading}>
              <Loading />
            </div>
          ) : courses.length ? (
            <div className={styles.list}>
              {courses.map((course) => {
                const isAttached = attachedCourseIds.has(course.id);
                const isNewAttach =
                  isAttached && !initialAttachedCourseIds.has(course.id);
                const showModuleRow =
                  isNewAttach && isModularCourse(course);
                const showOrderRow = isNewAttach;
                const isOrderRowDisabled =
                  isInteractionDisabled ||
                  (isModularCourse(course) && !draftModuleByCourseId[course.id]);
                const isModulesLoading = modulesLoadingCourseIds.has(course.id);
                const moduleOptions = moduleOptionsByCourseId[course.id] ?? [];
                const selectedModuleId = draftModuleByCourseId[course.id];
                const selectedModuleOption =
                  moduleOptions.find(
                    (option) => option.value === selectedModuleId,
                  ) ?? null;
                const orderOptions = orderOptionsByCourseId[course.id] ?? [];
                const selectedOrder = draftOrderByCourseId[course.id];
                const selectedOrderOption =
                  orderOptions.find(
                    (option) => option.value === String(selectedOrder),
                  ) ?? null;

                return (
                  <div key={course.id} className={styles.itemGroup}>
                    <div
                      className={cn(styles.item, {
                        [styles.itemDisabled]: isInteractionDisabled,
                      })}
                    >
                      <Checkbox
                        className={styles.check}
                        size="sm"
                        checked={isAttached}
                        disabled={isInteractionDisabled}
                        onChange={() => onCourseToggle(course, isAttached)}
                      />
                      <CoursePreview
                        className={styles.coursePreview}
                        preview={course.preview}
                      />
                      <div className={styles.courseInfo}>
                        <Typography
                          className={styles.courseTitle}
                          variant="body3"
                          weight="medium"
                          mode="compact"
                          title={course.title || undefined}
                        >
                          {course.title ||
                            getTranslation('common.text.untitled')}
                        </Typography>
                        {course.typeLabel && (
                          <Typography
                            className={styles.courseType}
                            variant="body3"
                            mode="compact"
                          >
                            {course.typeLabel}
                          </Typography>
                        )}
                      </div>
                      {course.school && (
                        <Tooltip
                          text={course.school.name}
                          triggerClassName={styles.schoolTrigger}
                          triggerElement={
                            <SchoolPreview
                              className={styles.schoolPreview}
                              preview={course.school.preview}
                              name={course.school.name}
                            />
                          }
                        />
                      )}
                    </div>
                    {showModuleRow && (
                      <div
                        className={cn(styles.moduleRow, {
                          [styles.moduleRowDisabled]: isInteractionDisabled,
                        })}
                      >
                        <Typography
                          className={styles.moduleLabel}
                          variant="body3"
                          weight="medium"
                          mode="compact"
                        >
                          {getTranslation(
                            'courses_lessons_page.attach_drawer.module_label',
                          )}
                        </Typography>
                        {isModulesLoading ? (
                          <div className={styles.moduleLoading}>
                            <Loading />
                          </div>
                        ) : (
                          <Select
                            className={styles.moduleSelect}
                            hideSelectedOptions={false}
                            options={moduleOptions}
                            value={selectedModuleOption}
                            placeholder={getTranslation(
                              'courses_lessons_page.attach_drawer.module_placeholder',
                            )}
                            disabled={isInteractionDisabled}
                            onChange={(option: ModuleSelectOption | null) => {
                              if (option?.value) {
                                onModuleChange(course.id, option.value);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}
                    {showOrderRow && (
                      <div
                        className={cn(styles.orderRow, {
                          [styles.orderRowDisabled]: isOrderRowDisabled,
                        })}
                      >
                        <Typography
                          className={styles.orderLabel}
                          variant="body3"
                          weight="medium"
                          mode="compact"
                        >
                          {getTranslation(
                            'courses_lessons_page.attach_drawer.order_label',
                          )}
                        </Typography>
                        <Select
                          className={styles.orderSelect}
                          hideSelectedOptions={false}
                          options={orderOptions}
                          value={selectedOrderOption}
                          placeholder={getTranslation(
                            'courses_lessons_page.attach_drawer.order_placeholder',
                          )}
                          disabled={isOrderRowDisabled}
                          onChange={(option: LessonOrderOption | null) => {
                            if (option?.value) {
                              onOrderChange(course.id, Number(option.value));
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Typography className={styles.empty} variant="body2">
              {getTranslation('courses_lessons_page.attach_drawer.empty')}
            </Typography>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            className={styles.action}
            size="md"
            variant="outlined"
            disabled={saving}
            onClick={onCancel}
          >
            {getTranslation('common.button.cancel')}
          </Button>
          <Button
            className={styles.action}
            size="md"
            disabled={!canSave || loading || saving}
            startIcon={
              saving ? <Loading color="var(--color-white-primary)" /> : undefined
            }
            onClick={onSave}
          >
            {getTranslation('common.button.save')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default React.memo(LessonAttachCoursesDrawer) as any;
